// Sound for the PS5 alerts: a synthesised bell and a spoken line. The
// dashboard runs on Windows, macOS, iOS and Android, and each puts sound behind
// its own rules, so they are handled in one place:
//
// - Every browser refuses sound until the page has been tapped or a key
//   pressed. iOS goes further: audio and speech must first be started INSIDE a
//   tap, after which they work from timers too. `unlock()` does exactly that on
//   the first tap anywhere, and alerts raised before it wait for it.
// - On an iPhone the ring/silent switch mutes web audio. Asking for a
//   "playback" audio session (iOS 17+) lets the bell through like a media app.
// - Voice lists differ per device and include novelty voices on Apple devices,
//   so the voice is chosen by name, not by whatever the browser lists first.
//
// Sound can be switched off per device (a phone in a pocket should not ring
// for the whole cafe); that choice is kept in this browser only.

type Alert = { text: string; bell: boolean };

const ENABLED_KEY = 'hhg.sound_alerts';
const CHANGE_EVENT = 'hhg:sound';

function readEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) !== 'off';
  } catch {
    return true;
  }
}

let enabled = readEnabled();
let unlocked = false;
const waiting: Alert[] = [];
let audio: AudioContext | null = null;

export type SoundState = 'off' | 'locked' | 'on';

export function soundState(): SoundState {
  if (!enabled) return 'off';
  return unlocked ? 'on' : 'locked';
}

export function subscribeSound(listener: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}

const notify = () => window.dispatchEvent(new Event(CHANGE_EVENT));

function audioContext(): AudioContext | null {
  if (audio) return audio;
  const Context =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Context) return null;
  audio = new Context();
  return audio;
}

// Must run inside a tap or key press.
export function unlock(): void {
  const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
  if (session) {
    try {
      session.type = 'playback';
    } catch {
      // Older iOS: the silent switch will mute the bell, speech still plays.
    }
  }

  const context = audioContext();
  if (context) {
    void context.resume();
    // A one-sample silent buffer is what actually opens audio on iOS.
    const source = context.createBufferSource();
    source.buffer = context.createBuffer(1, 1, 22050);
    source.connect(context.destination);
    source.start(0);
  }

  if ('speechSynthesis' in window) {
    const primer = new SpeechSynthesisUtterance(' ');
    primer.volume = 0;
    window.speechSynthesis.speak(primer);
  }

  if (!unlocked) {
    unlocked = true;
    notify();
  }
  while (waiting.length > 0) play(waiting.shift()!);
}

export function setSoundEnabled(on: boolean): void {
  enabled = on;
  try {
    localStorage.setItem(ENABLED_KEY, on ? 'on' : 'off');
  } catch {
    // Blocked storage: the choice lasts until the page is reloaded.
  }
  if (!on) {
    waiting.length = 0;
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }
  notify();
}

// Called from the sound button: switching on doubles as a test, so staff hear
// the bell and voice this device will use.
export function enableAndTest(): void {
  setSoundEnabled(true);
  unlock();
  play({ bell: true, text: 'Sound alerts are on.' });
}

// The first tap anywhere unlocks sound. pointerup/touchend rather than
// pointerdown: on touch screens only the end of a tap counts as one.
export function installUnlockListeners(): () => void {
  const events = ['pointerup', 'touchend', 'click', 'keydown'] as const;
  const handler = () => {
    if (enabled && (!unlocked || (audio !== null && audio.state !== 'running'))) unlock();
  };
  for (const event of events) window.addEventListener(event, handler, { passive: true });
  // iOS suspends audio when the app goes to the background; try to resume it
  // on return (the next tap unlocks it if this is refused).
  const onVisible = () => {
    if (document.visibilityState === 'visible' && audio?.state !== 'running') {
      void audio?.resume();
    }
  };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    for (const event of events) window.removeEventListener(event, handler);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

export function announce(alert: Alert): void {
  if (!enabled) return;
  if (!unlocked) {
    // Only the latest few matter if nobody has tapped for a while.
    waiting.push(alert);
    if (waiting.length > 5) waiting.shift();
    return;
  }
  play(alert);
}

function play(alert: Alert): void {
  if (!alert.bell) {
    speak(alert.text);
    return;
  }
  const ringing = ringBell();
  window.setTimeout(() => speak(alert.text), ringing);
}

// A struck bell, synthesised so there is no sound file to ship: three strikes,
// each a few inharmonic partials decaying at different rates. Returns how long
// it rings, so the spoken line can wait for it.
function ringBell(): number {
  const context = audioContext();
  if (!context) return 0;
  if (context.state === 'suspended') void context.resume();

  const partials = [
    { ratio: 1, gain: 0.5, decay: 1.6 },
    { ratio: 2.76, gain: 0.25, decay: 0.9 },
    { ratio: 5.4, gain: 0.12, decay: 0.5 },
    { ratio: 8.93, gain: 0.06, decay: 0.3 },
  ];
  const strikes = [0, 0.45, 0.9];
  const base = 880;
  const start = context.currentTime + 0.05;

  for (const offset of strikes) {
    for (const partial of partials) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      const at = start + offset;
      osc.type = 'sine';
      osc.frequency.value = base * partial.ratio;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(partial.gain, at + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + partial.decay);
      osc.connect(gain).connect(context.destination);
      osc.start(at);
      osc.stop(at + partial.decay + 0.05);
    }
  }
  return (strikes[strikes.length - 1] + 1) * 1000;
}

// macOS and iOS list joke voices among the English ones; never pick these.
const NOVELTY =
  /\b(albert|bad news|bahh|bells|boing|bubbles|cellos|deranged|good news|hysterical|jester|organ|superstar|trinoids|whisper|wobble|zarvox|junior|ralph|fred|kathy|princess|grandma|grandpa|rocko|eddy|flo|reed|sandy|shelley)\b/i;

// Clear, well-known voices by platform: Apple (Rishi, Veena, Samantha, Daniel,
// Karen), Windows (Neerja, Prabhat, Heera, Ravi, Aria, Jenny, Zira), and the
// Google voices on Chrome and Android.
const GOOD =
  /\b(rishi|veena|samantha|daniel|karen|moira|tessa|serena|neerja|prabhat|heera|ravi|aria|jenny|zira|google)\b/i;

// Voices not on this device ("Google …", "… Online (Natural)") are the
// clearest but need the internet. If one fails, the line is said again with
// the best voice that is installed locally, so a broken connection never
// silences a time-up.
function bestVoice(localOnly: boolean): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis
    .getVoices()
    .filter((voice) => /^en([-_]|$)/i.test(voice.lang))
    .filter((voice) => !NOVELTY.test(voice.name))
    .filter((voice) => !localOnly || voice.localService);
  const score = (voice: SpeechSynthesisVoice) =>
    (/natural|neural|enhanced|premium/i.test(voice.name) ? 16 : 0) +
    (GOOD.test(voice.name) ? 8 : 0) +
    (/^en[-_]in$/i.test(voice.lang) ? 4 : 0) +
    (/^en[-_](gb|us)$/i.test(voice.lang) ? 2 : 0) +
    (voice.default ? 1 : 0);
  return [...voices].sort((a, b) => score(b) - score(a))[0] ?? null;
}

// Chrome can garbage-collect an utterance mid-sentence and cut it off; holding
// a reference until it ends prevents that.
const speaking = new Set<SpeechSynthesisUtterance>();

function say(text: string, localOnly = false): void {
  const utterance = new SpeechSynthesisUtterance(text);
  const voice = bestVoice(localOnly);
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang;
  } else {
    utterance.lang = 'en-IN';
  }
  // Slower than conversation: it is heard across a noisy room.
  utterance.rate = 0.85;
  utterance.pitch = 1;
  utterance.volume = 1;
  speaking.add(utterance);
  utterance.onend = () => speaking.delete(utterance);
  utterance.onerror = (event) => {
    speaking.delete(utterance);
    if (!localOnly && voice && !voice.localService && event.error !== 'interrupted') {
      say(text, true);
    }
  };
  window.speechSynthesis.speak(utterance);
}

// Said twice: the first time catches attention, the second is the one people
// actually hear.
function speak(text: string): void {
  if (!('speechSynthesis' in window)) return;
  say(text);
  say(text);
}

// Voice lists load late on Chrome and Android; asking early has them ready by
// the first alert.
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.getVoices();
}

// "PS5-1" read literally comes out as "P S five minus one". Spell the console
// out and say the seat number as a number: "P S 5, number 1".
export function spokenStationName(name: string): string {
  return name
    .replace(/\bPS\s*5\b/gi, 'P S 5')
    .replace(/\s*[-_#]\s*(\d+)\b/g, ', number $1');
}
